import React, { useEffect, useState } from 'react';
import { CloseableModal } from '../../../../components/CloseableModal';
import { BodyWrapper } from './BodyWrapper';
import { Footer } from './Footer';
import { OfferLetterTemplate } from '../../../../entities/v1/applicant_tracking/OfferLetterTemplate';
import { ConnectEmailIntegrationModal } from '../../../SendEmailModal/ConnectEmailIntegrationModal';
import { buildUrl } from '../../../../utils/url';
import { SelectOption } from '../../../../components/Select';
import { OfferService } from '../../../../services/v1/applicant_tracking/OfferService';
import { Offer } from '../../../../entities/v1/applicant_tracking/Offer';
import { AlertObject, AlertType } from '../../../../components/Alert';
import { ApiError } from '../../../../services/ApiService/errors/ApiError';
import classNames from 'classnames';
import { getNounWithPossessiveCase } from '../../../../utils/grammar';
import { AttachmentDto } from '../../../../services/dtos/EmailDto';
import { FileContent, useImperativeFilePicker } from 'use-file-picker';
import {
  DEFAULT_CURRENCY,
  getCurrencySymbol,
} from '../../../../utils/currency';
import { EmailUserIntegration } from '../../../../entities/EmailUserIntegration';
import { UserService } from '../../../../services/v1/UserService';
import {
  isFailedIntegration,
  getConnectionEmailUrl,
} from '../../../../utils/espUserIntegration';
import { FilesSizeValidator } from '../../../../utils/useFilePicker/filesSizeValidator';
import { ReconnectEmailIntegrationModalBody } from '../../../../components/ReconnectEmailIntegrationModalBody';
import { FilesLimitValidator } from '../../../../utils/useFilePicker/filesLimitValidator';

interface PropTypes {
  applicationId: number;
  hasEmailConnected: boolean;
  isOpen: boolean;
  to: string;
  onClose: (succedded?: boolean) => void;
  setAlert: (alert: AlertObject) => void;
}

export interface OfferForm {
  attachments: AttachmentDto[];
  to: string;
  offerLetterTemplate?: OfferLetterTemplate;
  startingDate?: string;
  salary?: number;
  salaryCurrency?: string;
  equity?: number;
  workingHours?: number;
  cc?: SelectOption[];
  bcc?: SelectOption[];
  email?: string;
  emailJson?: string;
  subject?: string;
  subjectJson?: string;
}

const ATTACHMENT_MAX_FILES = 6;
const ATTACHMENT_MAX_SIZE_MB = 10;
const ATTACHMENT_MAX_FILES_SIZE_MB = 20;

const INITIAL_FORM = {
  attachments: [],
  to: null,
  offerLetterTemplate: null,
  startingDate: null,
  salary: null,
  salaryCurrency: DEFAULT_CURRENCY.label,
  equity: null,
  workingHours: null,
  cc: null,
  bcc: null,
  email: null,
  emailJson: null,
  subject: null,
  subjectJson: null,
};

function salaryWithCurrency(salary?: number, salaryCurrency?: string) {
  if (salary == null) {
    return null;
  }

  return `${getCurrencySymbol(salaryCurrency)}${salary}`;
}

function handlePreview(form: OfferForm, applicationId: number) {
  const previewUrl = buildUrl(
    `${window.location.origin}/applicant_tracking/offer_letter_templates/${form.offerLetterTemplate?.id}/preview`,
    [
      {
        key: 'salary',
        value: salaryWithCurrency(form.salary, form.salaryCurrency),
      },
      { key: 'equity', value: form.equity?.toString() },
      { key: 'start_date', value: form.startingDate },
      { key: 'working_hours', value: form.workingHours?.toString() },
      {
        key: 'application_id',
        value: applicationId.toString(),
      },
    ],
  );

  window.open(previewUrl, '_blank', 'noopener,noreferrer');
}

async function handleSubmit(
  form: OfferForm,
  applicationId: number,
  onClose: (succedded?: boolean) => void,
  setAlert: (alert: AlertObject) => void,
  setLoading: (loading: boolean) => void,
) {
  setLoading(true);
  await OfferService.create({
    applicationId: applicationId,
    emailHtml: form.email,
    offerLetterTemplateId: form.offerLetterTemplate.id,
    subjectText: form.subject,
    to: form.to,
    bcc: form.bcc?.map((c: SelectOption) => c.value),
    cc: form.cc?.map((c: SelectOption) => c.value),
    equity: form.equity,
    salary: salaryWithCurrency(form.salary, form.salaryCurrency),
    startingDate: form.startingDate,
    workingHours: form.workingHours,
    attachments: form.attachments,
  })
    .then((offer: Offer) => {
      setAlert({
        message: (
          <span>
            <b>
              {getNounWithPossessiveCase(
                offer.application?.candidate?.name || 'Candidate',
              )}
            </b>{' '}
            Offer for <b>{offer.application?.job?.name || 'the job'}</b> was
            successfully sent!
          </span>
        ),
        type: AlertType.Success,
      });
      setLoading(false);
      onClose(true);
    })
    .catch((e: ApiError) => {
      setAlert({
        message: e.body?.message || 'It was not possible to send the offer',
        type: AlertType.Danger,
      });
      setLoading(false);
      onClose(false);
    });
}

function buildTitle(step: number, failedIntegration: boolean) {
  if (failedIntegration) {
    return 'Email Integration Expired';
  }

  return step === 3 ? 'Offer Summary' : 'Send Offer';
}

export function SendOfferModal(props: PropTypes) {
  const [form, setForm] = useState<OfferForm>(INITIAL_FORM);
  const [step, setStep] = useState<number>(0);
  const [validStep, setValidStep] = useState<boolean>(false);
  const [isValid, setIsValid] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [emailIntegration, setEmailIntegration] =
    useState<EmailUserIntegration>(null);
  const [emailIntegrationsLoading, setEmailIntegrationsLoading] =
    useState<boolean>(true);
  const hasEmailConnected =
    (emailIntegrationsLoading || props.hasEmailConnected) &&
    isFailedIntegration(emailIntegration) === false;
  const bodyIdentifier = 'offer-email-body';

  const handleRemoveFile = (index: number) => {
    const fileToDelete = form.attachments[index];
    const plainFileToDelete = plainFiles.find(
      (i) => i.name === fileToDelete.filename,
    );

    if (plainFileToDelete != null) {
      removeFileByReference(plainFileToDelete);
    }

    setForm({
      ...form,
      attachments: form.attachments.filter((_i, idx) => idx !== index),
    });
  };

  const [
    openFileSelector,
    {
      filesContent,
      errors,
      removeFileByReference,
      plainFiles,
      clear: clearUploadedFiles,
    },
  ] = useImperativeFilePicker({
    multiple: true,
    readAs: 'DataURL',
    maxFileSize: ATTACHMENT_MAX_SIZE_MB,
    readFilesContent: true,
    limitFilesConfig: {
      max: ATTACHMENT_MAX_FILES,
    },
    validators: [
      new FilesSizeValidator(
        form.attachments.map((a) => a.size),
        ATTACHMENT_MAX_FILES_SIZE_MB,
      ),
      new FilesLimitValidator(form.attachments.length, ATTACHMENT_MAX_FILES),
    ],
  });

  useEffect(() => {
    const newAttachmentDto = [...form.attachments];

    filesContent.forEach((fileContent: FileContent, index: number) => {
      if (
        newAttachmentDto.find((i) => fileContent.name == i.filename) == null
      ) {
        newAttachmentDto.push({
          size: plainFiles[index].size,
          filename: fileContent.name,
          contentBase64: fileContent.content,
        });
      }
    });

    setForm({
      ...form,
      attachments: newAttachmentDto,
    });

    setIsValid(validStep);
  }, [filesContent, validStep]);

  useEffect(() => {
    const emailBodyContainer = document.querySelector(`#${bodyIdentifier}`);
    if (emailBodyContainer) {
      emailBodyContainer.scrollTop = emailBodyContainer.scrollHeight;
    }
  }, [form.attachments]);

  useEffect(() => {
    props.isOpen &&
      UserService.showEmailIntegration()
        .then(setEmailIntegration)
        .finally(() => setEmailIntegrationsLoading(false));
  }, [props.isOpen]);

  return (
    <CloseableModal
      size={step === 3 ? 'md' : 'lg'}
      isOpen={props.isOpen}
      className='modal-dialog-centered'
      onClose={props.onClose}
      headerTitle={buildTitle(step, isFailedIntegration(emailIntegration))}
      bodyClassName={classNames({ 'p-0': hasEmailConnected && step < 3 })}
      bodyChildren={
        isFailedIntegration(emailIntegration) ? (
          <ReconnectEmailIntegrationModalBody
            largeImage
            url={getConnectionEmailUrl()}
            onClose={props.onClose}
          />
        ) : hasEmailConnected ? (
          <BodyWrapper
            elementIdentifier={bodyIdentifier}
            applicationId={props.applicationId}
            form={form}
            isLoading={isLoading}
            to={props.to}
            step={step}
            clearUploadedFiles={clearUploadedFiles}
            setForm={setForm}
            setValidStep={setValidStep}
            files={plainFiles}
            removeAttachmentFileByIndex={handleRemoveFile}
          />
        ) : (
          <ConnectEmailIntegrationModal
            url={getConnectionEmailUrl()}
            onClose={() => {}}
          />
        )
      }
      footerClassName={step == 3 && 'd-flex justify-content-between'}
      footerChildren={
        hasEmailConnected && (
          <Footer
            filesCount={form.attachments.length}
            isValid={isValid}
            isLoading={emailIntegrationsLoading}
            step={step}
            setStep={setStep}
            onClose={props.onClose}
            onSubmit={() =>
              handleSubmit(
                form,
                props.applicationId,
                props.onClose,
                props.setAlert,
                setIsLoading,
              )
            }
            onPreview={() => handlePreview(form, props.applicationId)}
            openFileSelector={openFileSelector}
            attachmentErrors={errors}
            maxFiles={ATTACHMENT_MAX_FILES}
            maxFileSizeMb={ATTACHMENT_MAX_SIZE_MB}
          />
        )
      }
    />
  );
}
